import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  SVGArrowRight,
  SVGBarChart,
  SVGBuilding2,
  SVGCheckCircle,
  SVGChevronRight,
  SVGCode,
  SVGGlobe2,
  SVGHandShake,
  SVGHotel,
  SVGLayers,
  SVGLock,
  SVGMessage,
  SVGPalette,
  SVGShieldCheck,
  SVGUsers,
  SVGWallet,
  SVGZap,
  SVGBrain,
} from "../assets/PackageSVG";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import styles from "./ForBusinessPage.module.scss";

type RevealProps = {
  className?: string;
  children: React.ReactNode;
  tag?: "section" | "div";
};

function Reveal({ className, children, tag = "section" }: RevealProps) {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add(styles.fbVisible);
          obs.unobserve(el);
        }
      },
      { threshold: 0.12 },
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  if (tag === "div") {
    return (
      <div ref={ref as React.RefObject<HTMLDivElement>} className={`${styles.fbReveal} ${className || ""}`}>
        {children}
      </div>
    );
  }

  return (
    <section ref={ref as React.RefObject<HTMLElement>} className={`${styles.fbReveal} ${className || ""}`}>
      {children}
    </section>
  );
}

type CountUpProps = {
  target: number;
  suffix?: string;
  prefix?: string;
};

function CountUp({ target, suffix = "", prefix = "" }: CountUpProps) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const [val, setVal] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let cancelled = false;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        obs.unobserve(el);

        const dur = 1600;
        const start = performance.now();

        const step = (now: number) => {
          if (cancelled) return;
          const t = Math.min((now - start) / dur, 1);
          const ease = 1 - Math.pow(1 - t, 3);
          setVal(Math.round(ease * target));
          if (t < 1) requestAnimationFrame(step);
        };

        requestAnimationFrame(step);
      },
      { threshold: 0.3 },
    );

    obs.observe(el);
    return () => {
      cancelled = true;
      obs.disconnect();
    };
  }, [target]);

  return (
    <span ref={ref}>
      {prefix}
      {val}
      {suffix}
    </span>
  );
}

const iconProps = { width: "20", height: "20" };
const smallIconProps = { width: "18", height: "18" };

const AUDIENCE_TABS = [
  { key: "corporate", icon: <SVGBuilding2 {...iconProps} />, label: "Corporate Travel" },
  { key: "hotels", icon: <SVGHotel {...iconProps} />, label: "Hotels & Carriers" },
  { key: "agencies", icon: <SVGHandShake {...iconProps} />, label: "Travel Agencies" },
] as const;

type TabKey = (typeof AUDIENCE_TABS)[number]["key"];

type Feature = {
  icon: React.ReactNode;
  title: string;
  desc: string;
};

type AudienceContent = {
  headline: string;
  sub: string;
  cta: string;
  features: Feature[];
  mockup: TabKey;
};

const AUDIENCE_CONTENT: Record<TabKey, AudienceContent> = {
  corporate: {
    headline: "Your team travels smarter.",
    sub: "Give employees an AI travel assistant that books within policy, finds the best prices, and handles changes automatically.",
    cta: "Set up corporate account",
    features: [
      { icon: <SVGShieldCheck {...iconProps} />, title: "Policy-aware booking", desc: "Set budgets and rules, AI enforces them automatically." },
      { icon: <SVGBarChart {...iconProps} />, title: "Centralized dashboard", desc: "All employee trips in one view." },
      { icon: <SVGZap {...iconProps} />, title: "Real-time itinerary support", desc: "Changes, delays, and rebooking handled by AI." },
      { icon: <SVGWallet {...iconProps} />, title: "Expense-ready receipts", desc: "Every booking exports clean for accounting." },
      { icon: <SVGUsers {...iconProps} />, title: "Multi-traveler booking", desc: "Book for the whole team in one conversation." },
      { icon: <SVGMessage {...iconProps} />, title: "24/7 AI support", desc: "No hold music, no waiting." },
    ],
    mockup: "corporate",
  },
  hotels: {
    headline: "Get in front of millions of travelers.",
    sub: "List your properties or routes directly on Adiona and reach high-intent travelers through AI-powered recommendations.",
    cta: "Become a partner",
    features: [
      { icon: <SVGGlobe2 {...iconProps} />, title: "Direct listings", desc: "No OTA commission, connect directly with travelers." },
      { icon: <SVGBrain {...iconProps} />, title: "AI-powered visibility", desc: "Adiona recommends you based on fit, not just price." },
      { icon: <SVGZap {...iconProps} />, title: "Real-time inventory sync", desc: "Availability and pricing always accurate." },
      { icon: <SVGBarChart {...iconProps} />, title: "Performance analytics", desc: "See exactly how travelers find and book you." },
      { icon: <SVGLayers {...iconProps} />, title: "Dynamic pricing support", desc: "AI helps you price competitively in real time." },
      { icon: <SVGUsers {...iconProps} />, title: "Dedicated partner manager", desc: "A real human for onboarding and support." },
    ],
    mockup: "hotels",
  },
  agencies: {
    headline: "White-label Adiona for your clients.",
    sub: "Resell Adiona's AI travel platform under your own brand and deliver an advanced booking experience.",
    cta: "Apply for agency access",
    features: [
      { icon: <SVGPalette {...iconProps} />, title: "White-label ready", desc: "Your brand, your domain, Adiona's AI engine underneath." },
      { icon: <SVGLayers {...iconProps} />, title: "Full booking stack", desc: "Flights, hotels, cars, tours all included." },
      { icon: <SVGWallet {...iconProps} />, title: "Revenue share model", desc: "Earn on every booking your clients make." },
      { icon: <SVGBarChart {...iconProps} />, title: "Agency dashboard", desc: "Manage all client accounts from one place." },
      { icon: <SVGBrain {...iconProps} />, title: "Custom AI training", desc: "Tune the assistant to your agency's specialty." },
      { icon: <SVGCode {...iconProps} />, title: "Priority API access", desc: "Build custom integrations on top of Adiona." },
    ],
    mockup: "agencies",
  },
};

const STATS = [
  { value: 180, suffix: "+", label: "Countries covered" },
  { value: 2.4, suffix: "B", label: "In travel booked", prefix: "$" },
  { value: 94, suffix: "%", label: "Booking satisfaction" },
  { value: 3, suffix: " min", label: "Search to booking" },
];

const STEPS = [
  { num: "01", title: "Connect", desc: "Link your inventory, team, or client base via API or dashboard." },
  { num: "02", title: "Configure", desc: "Set policies, branding, pricing rules." },
  { num: "03", title: "Launch", desc: "Go live with your AI-powered travel experience." },
  { num: "04", title: "Optimize", desc: "Use analytics and AI insights to improve over time." },
];

const INTEGRATIONS = ["Amadeus", "Hotelbeds", "Agoda", "Stripe", "Apple Pay", "Google Pay", "Sabre", "Galileo"];

const TRUST_BADGES = [
  { icon: <SVGShieldCheck {...smallIconProps} />, label: "SOC 2 Type II" },
  { icon: <SVGLock {...smallIconProps} />, label: "GDPR Compliant" },
  { icon: <SVGLock {...smallIconProps} />, label: "256-bit Encryption" },
];

const PRICING = [
  {
    tier: "Starter",
    desc: "For small agencies or SMB corporate.",
    price: "Free",
    priceSub: "Commission-based",
    features: ["Up to 50 bookings/month", "AI travel assistant", "Basic analytics", "Email support", "Standard integrations"],
    cta: "Get started free",
    highlight: false,
  },
  {
    tier: "Business",
    desc: "For mid-size companies.",
    price: "$299",
    priceSub: "/mo + lower commission",
    features: ["Unlimited bookings", "Policy engine & approvals", "Advanced analytics", "Priority support", "Custom branding", "API access"],
    cta: "Start free trial",
    highlight: true,
    badge: "Most popular",
  },
  {
    tier: "Enterprise",
    desc: "For large corporates, hotel chains, airlines.",
    price: "Custom",
    priceSub: "Tailored to your scale",
    features: ["Unlimited everything", "Dedicated account manager", "Custom AI training", "SLA guarantee", "SSO & security", "White-label option"],
    cta: "Talk to sales",
    highlight: false,
  },
];

const TESTIMONIALS = [
  {
    quote: "Adiona cut our average booking time from 45 minutes to under 3. Our finance team loves the automatic expense exports.",
    name: "Sarah Chen",
    role: "VP of Operations",
    company: "Nextera Dynamics",
  },
  {
    quote: "Since listing on Adiona, our direct bookings increased 38%. The AI recommends us to the right travelers.",
    name: "Marco Bellini",
    role: "Revenue Manager",
    company: "Hotel Lido Collection",
  },
  {
    quote: "We white-labeled Adiona in two weeks. Our clients think it is our own platform and booking conversion is up.",
    name: "Priya Kapoor",
    role: "Founder & CEO",
    company: "Voyager Travel Co.",
  },
];

function CorporateMockup() {
  return (
    <div className={styles.fbMockup}>
      <div className={styles.fbMockupBar}>
        <span className={styles.fbMockupDot} />
        <span className={styles.fbMockupDot} />
        <span className={styles.fbMockupDot} />
        <span className={styles.fbMockupTitle}>Corporate Dashboard</span>
      </div>
      <div className={styles.fbMockupBody}>
        <div className={styles.fbMockStatRow}>
          <div className={styles.fbMockStat}><span className={styles.fbMockStatVal}>$24.8K</span><span className={styles.fbMockStatLbl}>This month</span></div>
          <div className={styles.fbMockStat}><span className={styles.fbMockStatVal}>142</span><span className={styles.fbMockStatLbl}>Active trips</span></div>
          <div className={styles.fbMockStat}><span className={styles.fbMockStatVal}>98%</span><span className={styles.fbMockStatLbl}>In policy</span></div>
        </div>
        <div className={styles.fbMockRows}>
          {["Alex Rivera - NYC to London", "Maya Chen - SFO to Tokyo", "James Park - LAX to Paris"].map((t, i) => (
            <div key={i} className={styles.fbMockRow}>
              <div className={styles.fbMockAvatar} />
              <span className={styles.fbMockRowText}>{t}</span>
              <span className={`${styles.fbMockBadge} ${i === 0 ? styles.fbMockBadgeGreen : i === 1 ? styles.fbMockBadgeBlue : styles.fbMockBadgeAmber}`}>
                {i === 0 ? "Booked" : i === 1 ? "Pending" : "Draft"}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function HotelsMockup() {
  return (
    <div className={styles.fbMockup}>
      <div className={styles.fbMockupBar}>
        <span className={styles.fbMockupDot} />
        <span className={styles.fbMockupDot} />
        <span className={styles.fbMockupDot} />
        <span className={styles.fbMockupTitle}>Partner Analytics</span>
      </div>
      <div className={styles.fbMockupBody}>
        <div className={styles.fbMockStatRow}>
          <div className={styles.fbMockStat}><span className={styles.fbMockStatVal}>2,847</span><span className={styles.fbMockStatLbl}>Views this week</span></div>
          <div className={styles.fbMockStat}><span className={styles.fbMockStatVal}>312</span><span className={styles.fbMockStatLbl}>Bookings</span></div>
          <div className={styles.fbMockStat}><span className={styles.fbMockStatVal}>+18%</span><span className={styles.fbMockStatLbl}>vs last month</span></div>
        </div>
        <div className={styles.fbMockChart}>
          {[40, 55, 45, 70, 60, 85, 78].map((h, i) => (
            <div key={i} className={styles.fbMockBarCol}>
              <div className={styles.fbMockBar} style={{ height: `${h}%` }} />
              <span className={styles.fbMockBarLabel}>{["M", "T", "W", "T", "F", "S", "S"][i]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AgenciesMockup() {
  return (
    <div className={styles.fbMockup}>
      <div className={styles.fbMockupBar}>
        <span className={styles.fbMockupDot} />
        <span className={styles.fbMockupDot} />
        <span className={styles.fbMockupDot} />
        <span className={styles.fbMockupTitle}>Agency Console</span>
      </div>
      <div className={styles.fbMockupBody}>
        <div className={styles.fbMockStatRow}>
          <div className={styles.fbMockStat}><span className={styles.fbMockStatVal}>86</span><span className={styles.fbMockStatLbl}>Active clients</span></div>
          <div className={styles.fbMockStat}><span className={styles.fbMockStatVal}>$12.4K</span><span className={styles.fbMockStatLbl}>Revenue share</span></div>
          <div className={styles.fbMockStat}><span className={styles.fbMockStatVal}>4.9</span><span className={styles.fbMockStatLbl}>Client rating</span></div>
        </div>
        <div className={styles.fbMockRows}>
          {["Luxe Escapes - 24 travelers", "Summit Corp - 18 travelers", "Coastal Retreats - 12 travelers"].map((t, i) => (
            <div key={i} className={styles.fbMockRow}>
              <div className={`${styles.fbMockAvatar} ${styles.fbMockAvatarAgency}`} />
              <span className={styles.fbMockRowText}>{t}</span>
              <SVGChevronRight width="14" height="14" className={styles.fbMockChevron as any} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const MOCKUPS: Record<TabKey, () => JSX.Element> = {
  corporate: CorporateMockup,
  hotels: HotelsMockup,
  agencies: AgenciesMockup,
};

export const ForBusinessPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabKey>("corporate");
  const content = AUDIENCE_CONTENT[tab];
  const MockupComponent = MOCKUPS[tab];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={styles.fb}>
      <Header />

      <section className={styles.fbHero}>
        <div className={styles.fbHeroBg} />
        <div className={styles.fbHeroInner}>
          <h1 className={styles.fbHeroH}>Travel infrastructure for modern businesses.</h1>
          <p className={styles.fbHeroSub}>
            Whether you manage corporate travel, run a travel agency, or operate hotels and flights - Adiona gives you AI tools to do it better.
          </p>
          <div className={styles.fbHeroActions}>
            <button className={styles.fbBtnPrimary} onClick={() => navigate("/chat")}>
              Get started <SVGArrowRight width="16" height="16" />
            </button>
            <button className={styles.fbBtnOutline} onClick={() => navigate("/about")}>
              Talk to team
            </button>
          </div>
        </div>
      </section>

      <Reveal className={styles.fbTabsSection}>
        <div className={styles.fbTabs}>
          {AUDIENCE_TABS.map((t) => (
            <button
              key={t.key}
              className={`${styles.fbTab} ${tab === t.key ? styles.fbTabActive : ""}`}
              onClick={() => setTab(t.key)}
              type="button"
            >
              {t.icon}
              <span>{t.label}</span>
            </button>
          ))}
        </div>
      </Reveal>

      <Reveal className={styles.fbAudience}>
        <div className={styles.fbAudienceInner}>
          <div className={styles.fbAudienceText}>
            <h2 className={styles.fbAudienceH}>{content.headline}</h2>
            <p className={styles.fbAudienceSub}>{content.sub}</p>
            <div className={styles.fbFeaturesGrid}>
              {content.features.map((f, i) => (
                <div key={i} className={styles.fbFeature}>
                  <div className={styles.fbFeatureIcon}>{f.icon}</div>
                  <div>
                    <div className={styles.fbFeatureTitle}>{f.title}</div>
                    <div className={styles.fbFeatureDesc}>{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <button className={`${styles.fbBtnPrimary} ${styles.fbAudienceCta}`} onClick={() => navigate("/chat")}>
              {content.cta} <SVGArrowRight width="16" height="16" />
            </button>
          </div>
          <div className={styles.fbAudienceMockup}>
            <MockupComponent />
          </div>
        </div>
      </Reveal>

      <Reveal className={styles.fbStats} tag="div">
        <div className={styles.fbStatsInner}>
          {STATS.map((s, i) => (
            <div key={i} className={styles.fbStat}>
              <div className={styles.fbStatVal}>
                <CountUp target={s.value} suffix={s.suffix} prefix={s.prefix} />
              </div>
              <div className={styles.fbStatLabel}>{s.label}</div>
            </div>
          ))}
        </div>
      </Reveal>

      <Reveal className={styles.fbStepsSection}>
        <div className={styles.fbSectionHead}>
          <h2 className={styles.fbSectionTitle}>Up and running in days, not months.</h2>
        </div>
        <div className={styles.fbSteps}>
          {STEPS.map((s, i) => (
            <React.Fragment key={s.num}>
              <div className={styles.fbStep}>
                <div className={styles.fbStepNum}>{s.num}</div>
                <div className={styles.fbStepTitle}>{s.title}</div>
                <div className={styles.fbStepDesc}>{s.desc}</div>
              </div>
              {i < STEPS.length - 1 && <div className={styles.fbStepLine} />}
            </React.Fragment>
          ))}
        </div>
      </Reveal>

      <Reveal className={styles.fbIntegrations}>
        <div className={styles.fbSectionHead}>
          <h2 className={styles.fbSectionTitle}>Built on infrastructure you already trust.</h2>
        </div>
        <div className={styles.fbLogoRow}>
          {INTEGRATIONS.map((name) => (
            <div key={name} className={styles.fbLogoBadge}>{name}</div>
          ))}
        </div>
        <p className={styles.fbTrustLine}>Enterprise-grade security. 99.9% uptime SLA. GDPR compliant.</p>
        <div className={styles.fbTrustBadges}>
          {TRUST_BADGES.map((b, i) => (
            <div key={i} className={styles.fbTrustBadge}>
              {b.icon}
              <span>{b.label}</span>
            </div>
          ))}
        </div>
      </Reveal>

      <Reveal className={styles.fbPricing}>
        <div className={styles.fbSectionHead}>
          <h2 className={styles.fbSectionTitle}>Simple, transparent pricing.</h2>
        </div>
        <div className={styles.fbPricingGrid}>
          {PRICING.map((p) => (
            <div key={p.tier} className={`${styles.fbPriceCard} ${p.highlight ? styles.fbPriceCardHighlighted : ""}`}>
              {p.badge && <div className={styles.fbPriceBadge}>{p.badge}</div>}
              <div className={styles.fbPriceTier}>{p.tier}</div>
              <div className={styles.fbPriceDesc}>{p.desc}</div>
              <div className={styles.fbPriceAmount}>
                <span className={styles.fbPriceNum}>{p.price}</span>
                <span className={styles.fbPriceSub}>{p.priceSub}</span>
              </div>
              <ul className={styles.fbPriceFeatures}>
                {p.features.map((f, j) => (
                  <li key={j}><SVGCheckCircle width="16" height="16" /> {f}</li>
                ))}
              </ul>
              <button
                className={`${styles.fbPriceCta} ${p.highlight ? styles.fbPriceCtaPrimary : ""}`}
                onClick={() => navigate(p.tier === "Enterprise" ? "/about" : "/chat")}
              >
                {p.cta} <SVGArrowRight width="14" height="14" />
              </button>
            </div>
          ))}
        </div>
      </Reveal>

      <Reveal className={styles.fbTestimonials}>
        <div className={styles.fbSectionHead}>
          <h2 className={styles.fbSectionTitle}>Trusted by travel teams worldwide.</h2>
        </div>
        <div className={styles.fbTestimonialsGrid}>
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className={styles.fbTestimonial}>
              <div className={styles.fbTestimonialQuote}>"{t.quote}"</div>
              <div className={styles.fbTestimonialAuthor}>
                <div className={styles.fbTestimonialAvatar}>{t.name[0]}</div>
                <div>
                  <div className={styles.fbTestimonialName}>{t.name}</div>
                  <div className={styles.fbTestimonialRole}>{t.role}, {t.company}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Reveal>

      <Reveal className={styles.fbCtaFinal}>
        <div className={styles.fbCtaInner}>
          <h2 className={styles.fbCtaH}>Ready to bring AI to your travel business?</h2>
          <div className={styles.fbCtaActions}>
            <button className={styles.fbBtnPrimary} onClick={() => navigate("/chat")}>
              Get started <SVGArrowRight width="16" height="16" />
            </button>
            <button className={styles.fbBtnOutline} onClick={() => navigate("/about")}>
              Schedule a demo
            </button>
          </div>
          <p className={styles.fbCtaSub}>No long contracts. Cancel anytime. Setup in under a day.</p>
        </div>
      </Reveal>

      <Footer />
    </div>
  );
};

